import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../components/dashboard/DashboardStates";
import { useToast } from "../../../context/ToastContext";
import * as blogApi from "../../../api/blogApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { formatBlogDate } from "../../../utils/blog";
import BlogFormModal from "./BlogFormModal";

const BlogManager = () => {
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await blogApi.getBlogPosts(1, 100);
      setPosts(data?.items || []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) => {
      const en = p.translations?.find((t) => t.languageCode === "en");
      return (
        p.slug?.toLowerCase().includes(q) ||
        en?.title?.toLowerCase().includes(q) ||
        en?.category?.toLowerCase().includes(q)
      );
    });
  }, [posts, query]);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await blogApi.updateBlogPost(editing.id, {
          ...payload,
          rowVersion: editing.rowVersion,
        });
        toast.success("Blog post updated.");
      } else {
        await blogApi.createBlogPost(payload);
        toast.success("Blog post created.");
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await blogApi.deleteBlogPost(deleting.id);
      toast.success("Blog post deleted.");
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const titleOf = (post) =>
    post.translations?.find((t) => t.languageCode === "en")?.title ||
    post.translations?.[0]?.title ||
    post.slug;

  return (
    <DashboardLayout
      title="Blog"
      subtitle="Create and edit multilingual news posts shown on the public site."
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search posts…"
          className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
        />
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Add blog post
        </button>
      </div>

      {loading ? <LoadingState label="Loading blog posts…" /> : null}
      {!loading && error ? <ErrorState message={error} /> : null}

      {!loading && !error && filtered.length === 0 ? (
        <EmptyState
          title="No blog posts yet"
          description="Add your first multilingual news article."
          action={
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
              className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              Add blog post
            </button>
          }
        />
      ) : null}

      {!loading && !error && filtered.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Post</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Published</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((post) => (
                <tr key={post.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="flex gap-3">
                      <img
                        src={resolveMediaUrl(post.coverImageUrl)}
                        alt=""
                        className="h-14 w-20 rounded-xl object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900">{titleOf(post)}</p>
                        <p className="truncate text-xs text-slate-500">/{post.slug}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {post.isFeatured ? (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                              Featured
                            </span>
                          ) : null}
                          {post.showOnHome ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              Home
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        "rounded-full px-2.5 py-1 text-xs font-semibold",
                        post.isPublished
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600",
                      ].join(" ")}
                    >
                      {post.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatBlogDate(post.publishedAtUtc, "en")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/news/${post.slug}`}
                        target="_blank"
                        className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(post);
                          setModalOpen(true);
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(post)}
                        className="rounded-xl border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <BlogFormModal
        open={modalOpen}
        initial={editing}
        busy={busy}
        onClose={() => {
          if (busy) return;
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete blog post"
        description={
          deleting
            ? `Delete “${titleOf(deleting)}”? This cannot be undone.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </DashboardLayout>
  );
};

export default BlogManager;
